import "lib/glamor-speedy";
import browserUpdate from "browser-update";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";

browserUpdate({
    vs: { i: 10, f: -4, o: -4, s: -2, c: -4 },
    test: false,
    api: 4,
});

import ReactDOM from "react-dom";
// import React from "react";

import "../styles/index.styl";

import * as Sentry from "@sentry/browser";
import { Integrations } from "@sentry/tracing";
import getRouter from "../router";
import Brands from "collections/brands";
import app from "lib/app";
import config from "../site-config";

if (config.sentry) {
    Sentry.init({
        dsn: config.sentry,
        integrations: [
            new Integrations.BrowserTracing({
                // Track page changes. We can decide to disable later.
                startTransactionOnLocationChange: false,
                // Track page load. We can decide to disable later.
                startTransactionOnPageLoad: false,
            }),
            new Sentry.Integrations.Breadcrumbs({
                // Don't send console levels to Sentry. It's too noisy on development build
                console: process.env.NODE_ENV !== "development",
            }),
        ],
        tracesSampleRate: process.env.NODE_ENV === "development" ? 1.0 : 0.2,
        release: process.env.version,
        environment: process.env.NODE_ENV,
        allowUrls: [/nuorder\.com/gi, /[^.\s]+\.nuorder\.com/gi],
        denyUrls: [
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
        ignoreErrors: [
            // Random plugins/extensions
            "top.GLOBALS",
            // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error. html
            "originalCreateNotification",
            "canvas.contentDocument",
            "MyApp_RemoveAllHighlights",
            "http://tt.epicplay.com",
            "Can't find variable: ZiteReader",
            "jigsaw is not defined",
            "ComboSearch is not defined",
            "http://loading.retry.widdit.com/",
            "atomicFindClose",
            // Facebook borked
            "fb_xd_fragment",
            // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
            // reduce this. (thanks @acdha)
            // See http://stackoverflow.com/questions/4113268
            "bmi_SafeAddOnload",
            "EBCallBackMessageReceived",
            // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
            "conduitPage",
            "ResizeObserver",
            // See https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/17
            "Non-Error promise rejection captured",

            // TODO: Remove this as part of NU-33955
            "undefined is not an object (evaluating 'e.tags.product",
            "Cannot set properties of undefined (setting 'product')",
            "e.tags is undefined",
        ],
        beforeSend(event) {
            if (undefined !== event) {
                if (event?.tags) {
                    if (event.request.url.includes("/assortment") || event.request.url.includes("/drive")) {
                        event.tags.product = "Retail";
                    } else if (event.request.url.includes("/brand")) {
                        event.tags.product = "Brand";
                    } else {
                        event.tags.product = "Unknown";
                    }
                }
                return event;
            }
        },
    });
}

import { postToWindow } from "../helpers/window-messages";

// If backend redirects to frontend and user is logged in
postToWindow({ action: "checkForRecursiveIframe" });

import analytics from "analytics.js-loader"; // analytics
import { LAUNCH_DARKLY_ANONYMOUS_USER_KEY } from "../constants/login"; // segment

app.brands = new Brands();

// Listen to legacy events
window.addEventListener("message", app.legacy.onMessage, false);

if (config.analytics) {
    try {
        analytics({ writeKey: config.analytics, skipPageCall: true });
    } catch (e) {
        window.analytics = {
            track: () => {
                // no-op
            },
            group: () => {
                // no-op
            },
            page: () => {
                // no-op
            },
            identify: () => {
                // no-op
            },
        };
    }
} else {
    window.analytics = {
        track: () => {
            // no-op
        },
        group: () => {
            // no-op
        },
        page: () => {
            // no-op
        },
        identify: () => {
            // no-op
        },
    };
}

// Hide header if zoomed in to input or textarea
if ("ontouchstart" in window || navigator.msMaxTouchPoints) {
    document.addEventListener(
        "blur",
        (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.nodeName)) {
                setTimeout(() => {
                    document.body.classList.remove("mobileFixedFix");
                }, 23);
            }
        },
        true,
    );
    document.addEventListener(
        "focus",
        (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.nodeName)) {
                setTimeout(() => {
                    document.body.classList.add("mobileFixedFix");
                }, 100);
            }
        },
        true,
    );
}
/*
// Clear MFA redirect flag at the beginning of each session
// Also check if coming from LSID login redirect
window.addEventListener("load", () => {
    // Additional check: if coming from LSID login redirect, clear the flag
    // We are clearing out the MFA suggestion flag for referrers:
    // 'https://app.stg02.nuorder.com/'
    // 'https://id.stg02.nuorder.com/'
    // Happens during ULP page redirect
    const { referrer } = document;
    const { baseUrl } = config;

    if (
        referrer &&
        referrer.includes(baseUrl.replace("app", "id"))
        // ||
        //    (referrer.includes(baseUrl) && !referrer.includes("/brand"))
    ) {
        window.localStorage.setItem(MFA_REDIRECT_FLAG, "ready_to_show");
    }
});*/

// Now that we know a few things, like who to talk to
// Lets grab the user and see if they are logged in or now (which we will pass on to the router)

if (config.LAUNCH_DARKLY_CLIENT_ID) {
    (async () => {
        const LDProvider = await asyncWithLDProvider({
            clientSideID: config.LAUNCH_DARKLY_CLIENT_ID,
            user: {
                key: LAUNCH_DARKLY_ANONYMOUS_USER_KEY,
                anonymous: true,
                userAgent: navigator.userAgent,
            },
            options: {
                bootstrap: "localStorage",
            },
        });
        ReactDOM.render(getRouter(LDProvider), document.getElementById("app-root"));
    })();
} else {
    ReactDOM.render(getRouter(), document.getElementById("app-root"));
}
